import { HttpTransport } from "./transports/http";
import { clampMessageLength, ensureHttpsUrl } from "./utils/guards";
import { decodeJwtPayload } from "./utils/jwt";
import { uuidv4 } from "./utils/ids";
import type { ChatClientConfig, ClientEvent, SendMessageParams } from "./types";
import { ChatAuthError, ChatInProgressError, RateLimitedError } from "./types";

type EventHandler = (ev: ClientEvent) => void;

export class ChatClient {
  private readonly baseUrl: string;
  private readonly http: HttpTransport;
  private token: string | null = null;
  private tokenExp: number | null = null;
  private readonly timeoutMs: number;
  private readonly listeners = new Map<ClientEvent["type"], Set<EventHandler>>();
  private readonly inflight = new Map<string, boolean>();
  private readonly siteId: string;
  private readonly logger?: (level: "debug" | "info" | "warn" | "error", ...args: unknown[]) => void;
  private sessionId: string | null = null;
  private readonly chatIdStorageKey: string;

  constructor(cfg: ChatClientConfig) {
    this.timeoutMs = cfg.timeoutMs ?? 10000;
    this.siteId = cfg.siteId;
    this.logger = cfg.logger;
    this.chatIdStorageKey = `kennisbank_chat_id:${this.siteId}`;

    const url = ensureHttpsUrl(cfg.baseUrl);
    this.baseUrl = url.toString().replace(/\/$/, "");
    this.http = new HttpTransport(this.baseUrl, this.timeoutMs);
  }

  on(event: ClientEvent["type"], handler: EventHandler): () => void {
    const set = this.listeners.get(event) ?? new Set<EventHandler>();
    set.add(handler);
    this.listeners.set(event, set);
    return () => {
      const current = this.listeners.get(event);
      if (!current) {
        return;
      }
      current.delete(handler);
      if (current.size === 0) {
        this.listeners.delete(event);
      }
    };
  }

  private emit(ev: ClientEvent): void {
    const handlers = this.listeners.get(ev.type);
    if (!handlers) {
      return;
    }
    handlers.forEach((handler) => handler(ev));
  }

  private setToken(token: string): void {
    this.token = token;
    const decoded = decodeJwtPayload(token);
    this.tokenExp = decoded.exp ?? null;
  }

  async connect(): Promise<void> {
    const session = await this.http.createSession(this.siteId);
    this.setToken(session.token);
    this.sessionId = session.session_id;
    this.persistChatId(session.session_id);
    this.logger?.("info", "Connected to chatbot backend");
  }

  private async ensureFreshSession(): Promise<void> {
    const refreshBeforeSeconds = 60;
    const nowSeconds = Math.floor(Date.now() / 1000);
    if (!this.token || !this.tokenExp || this.tokenExp <= nowSeconds) {
      await this.connect();
      return;
    }
    if (this.tokenExp - nowSeconds <= refreshBeforeSeconds) {
      const session = await this.http.refreshSession(this.token);
      this.setToken(session.token);
      this.sessionId = session.session_id;
      this.persistChatId(session.session_id);
    }
  }

  startConversation(chatId?: string): string {
    if (chatId) {
      this.persistChatId(chatId);
      return chatId;
    }
    const existing = this.loadPersistedChatId();
    if (existing) {
      return existing;
    }
    if (this.sessionId) {
      this.persistChatId(this.sessionId);
      return this.sessionId;
    }
    const generated = uuidv4();
    this.persistChatId(generated);
    return generated;
  }

  async sendMessage(params: SendMessageParams): Promise<string> {
    const { chatId, message, onChunk, onDone, onStep, signal } = params;
    if (this.inflight.get(chatId)) {
      throw new ChatInProgressError();
    }
    this.inflight.set(chatId, true);
    try {
      await this.ensureFreshSession();
      if (!this.token || !this.sessionId) {
        throw new ChatAuthError();
      }
      const msg = clampMessageLength(message);
      return await this.http.sendMessageStream(
        this.token,
        this.sessionId,
        msg,
        onChunk,
        onDone,
        onStep,
        signal,
      );
    } catch (error) {
      if (error instanceof RateLimitedError) {
        this.emit({ type: "rate_limit", chatId, detail: error.message });
      } else {
        this.emit({ type: "error", error, chatId });
      }
      throw error;
    } finally {
      this.inflight.delete(chatId);
    }
  }

  async injectMessage(chatId: string, role: "user" | "model", text: string): Promise<void> {
    await this.ensureFreshSession();
    if (!this.token || !this.sessionId) {
      throw new ChatAuthError();
    }
    await this.http.injectMessage(this.token, this.sessionId, role, text);
  }

  async close(): Promise<void> {
    this.token = null;
    this.tokenExp = null;
    this.sessionId = null;
  }

  private persistChatId(chatId: string): void {
    globalThis.localStorage?.setItem(this.chatIdStorageKey, chatId);
  }

  private loadPersistedChatId(): string | null {
    return globalThis.localStorage?.getItem(this.chatIdStorageKey) ?? null;
  }
}

export * from "./types";
